import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { alpha, Box, Button, Grid, IconButton, InputBase, Popover, Stack, Tooltip, styled, Tab, Tabs, Typography } from '@mui/material';
import tokens from '../../styles/tokens';
import { useDispatch, useSelector } from 'react-redux';
import Navbar from '../../components/Navbar';
import { API } from '../../api/api_setup';
import DataDisplay from '../../components/DataDisplay';
import { countFormatterWithCommas, dataSizeFormatterInPB } from '../../utils/helpers';
import NumberDisplayCard from '../../components/NumberDisplayCard';
import { setClusters as setDashboardClusters, setPOVSortModel, setProdSortModel, setSyncInfo, setIsViewOwnedCluster } from './dashboardSlice';
import { setClusters as setClusterSettings, updateCluster as updateClusterSettings } from '../clusters/clusterSlice';
import { HomePageColumns } from '../../utils/constants'
import { useAuth } from '../../hooks/useAuth';
import { useSnackbar } from '../../hooks/useSnackbar';
import { useNavigate } from 'react-router-dom';
import InfoDisplayCard from '../../components/InfoDisplayCard';
import DrawerMenu from '../../components/DrawerMenu';
import SearchIcon from '@mui/icons-material/Search';
import CustomTabPanel from '../../components/CustomTabPanel';
import ClusterSettingsInline from '../../components/ClusterSettingsInline';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';

const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  '&:hover': {
    backgroundColor: alpha(theme.palette.common.white, 0.25),
  },
  marginLeft: 0,
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    marginLeft: theme.spacing(1),
    width: 'auto',
  },
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  width: '100%',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    // vertical padding + font size from searchIcon
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width'),
    [theme.breakpoints.up('sm')]: {
      width: '12ch',
      '&:focus': {
        width: '20ch',
      },
    },
  },
}));



const Dashboard = () => {
  const [isLoading, setIsLoading] = useState(true)
  const { user, logout } = useAuth()
  const axiosConfig = useMemo(() => ({
    headers: {
      Authorization: `Bearer ${user.token}`
    }
  }), [user.token])
  const prodSortModel = useSelector((state) => state.dashboard.prodSortModel)
  const povSortModel = useSelector((state) => state.dashboard.povSortModel)
  const clusters = useSelector((state) => state.dashboard.clusters)
  const isViewOwnedCluster = useSelector((state) => state.dashboard.isViewOwnedCluster)
  const syncInfo = useSelector((state) => state.dashboard.syncInfo)
  const clusterSettings = useSelector((state) => state.cluster.clusters)
  const clusterSettingsMap = useMemo(() => {
    const map = {}
    clusterSettings.forEach((cluster) => {
      map[cluster.clusterName] = cluster
    })
    return map
  }, [clusterSettings])
  const [searchCluster, setSearchCluster] = useState("")
  const [disableViewStateButton, setDisableViewStateButton] = useState(false)
  const [viewStateButtonText, setViewStateButtonText] = useState("View Owned")
  const [clusterSettingsDirty, setClusterSettingsDirty] = useState(false)
  const [isSavingClusterSettings, setIsSavingClusterSettings] = useState(false)
  const [settingsAnchorEl, setSettingsAnchorEl] = useState(null)
  const [settingsCluster, setSettingsCluster] = useState(null)

  const dispatch = useDispatch()
  const { showSnackbar } = useSnackbar()
  const navigate = useNavigate()
  const [tabValue, setTabValue] = useState(0);
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  function a11yProps(index) {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
    };
  }

  const handleProdSortModelChange = (model, details) => {
    dispatch(setProdSortModel(model))
  }
  const handlePovSortModelChange = (model, details) => {
    dispatch(setPOVSortModel(model))
  }

  const visibleClusters = useMemo(() => {
    if (isViewOwnedCluster) {
      const ownedClustersList = clusters.filter((cluster) => {
        const state = clusterSettingsMap[cluster.clusterName]
        if (state && typeof state.isOwned === 'boolean') {
          return state.isOwned
        }
        return cluster.isOwned
      })
      if (ownedClustersList.length > 0 || clusters.length === 0) {
        return ownedClustersList
      }
      return clusters
    }
    return clusters
  }, [clusters, clusterSettingsMap, isViewOwnedCluster])

  const enrichedClusters = useMemo(() => (
    visibleClusters.map((cluster) => {
      const state = clusterSettingsMap[cluster.clusterName]
      return {
        ...cluster,
        displayIsInternal: state?.isInternal ?? cluster.isInternal ?? false,
        displayIsProduction: state?.isProduction ?? cluster.isProduction ?? false,
        displayIsOwned: state?.isOwned ?? cluster.isOwned ?? false,
      }
    })
  ), [clusterSettingsMap, visibleClusters])

  const filterBySearch = useCallback((clusterList) => {
    if (!searchCluster) {
      return clusterList
    }
    return clusterList.filter(({ clusterName }) =>
      clusterName.toLowerCase().includes(searchCluster.toLowerCase())
    )
  }, [searchCluster])

  const handleClusterStateChange = useCallback((payload) => {
    setClusterSettingsDirty(true)
    dispatch(updateClusterSettings(payload))
    setSettingsCluster((prev) => {
      if (prev && prev.clusterName === payload.clusterName) {
        const displayKey = `display${payload.fieldName.charAt(0).toUpperCase()}${payload.fieldName.slice(1)}`
        return {
          ...prev,
          [displayKey]: payload.fieldValue
        }
      }
      return prev
    })
  }, [dispatch])

  const handleOpenClusterSettings = useCallback((event, cluster) => {
    event.preventDefault()
    event.stopPropagation()
    setSettingsAnchorEl(event.currentTarget)
    setSettingsCluster(cluster)
  }, [])

  const handleCloseClusterSettings = useCallback(() => {
    setSettingsAnchorEl(null)
    setSettingsCluster(null)
  }, [])

  const get_total_scanned_count = () => {
    let total = 0;
    for (const item of visibleClusters) {
      total += item.overallScanned
    }
    return countFormatterWithCommas(total)
  }

  const get_total_data_monitored = () => {
    let total = 0;
    for (const item of visibleClusters) {
      if (item.totalGb !== undefined && item.totalGb !== null) {
        total += item.totalGb
      }
    }
    return dataSizeFormatterInPB(total)
  }
  const handleCellClick = (params, event, details) => {

  }
  const callGetClusterOverViewApi = useCallback((showOwned = false) => {
    setDisableViewStateButton(true)
    setIsLoading(true)
    const endpoint = showOwned ? "/cluster/overview/owned" : "/cluster/overview"
    API.get(endpoint, axiosConfig)
    .then((response) => {
      dispatch(setDashboardClusters(response.data.data))
      setIsLoading(false)
      setDisableViewStateButton(false)
    })
    .catch(err => {
      if (err.response && err.response.status === 401) {
      showSnackbar("Unauthorized. Please log in again.")
      logout()
      navigate("/")
    } else {
      showSnackbar("Something went wrong !!")
    }
    setDisableViewStateButton(false)
    setIsLoading(false)
    })
  }, [axiosConfig, dispatch, logout, navigate, showSnackbar])
  const viewStateButtonClickHandler = () => {
    dispatch(setIsViewOwnedCluster(!isViewOwnedCluster))
  }

  const getProductionClustersData = () => {
    return filterBySearch(enrichedClusters.filter(({isProduction}) => isProduction))
  }

  const getPOVClustersData = () => {
    return filterBySearch(enrichedClusters.filter(({isProduction}) => !isProduction))
  }

  const handleSaveClusterSettings = useCallback(() => {
    if (clusterSettings.length === 0) {
      return
    }
    setIsSavingClusterSettings(true)
    API.patch('/cluster/patch', clusterSettings, axiosConfig)
      .then(() => {
        showSnackbar('Updated !!')
        setClusterSettingsDirty(false)
        callGetClusterOverViewApi(isViewOwnedCluster)
      })
      .catch(err => {
        showSnackbar('Something went wrong !!')
      })
      .finally(() => {
        setIsSavingClusterSettings(false)
      })
  }, [axiosConfig, callGetClusterOverViewApi, clusterSettings, isViewOwnedCluster, showSnackbar])

  useEffect(() => {
    callGetClusterOverViewApi(isViewOwnedCluster)
  }, [callGetClusterOverViewApi, isViewOwnedCluster])

  useEffect(() => {
    if (isViewOwnedCluster) {
      setViewStateButtonText("View All")
    } else {
      setViewStateButtonText("View Owned")
    }
  }, [isViewOwnedCluster])

  useEffect(() => {
    API.get("/sync/last", axiosConfig)
      .then((response) => {
        dispatch(setSyncInfo(response.data.data))
      })
      .catch(err => {})
  }, [axiosConfig, dispatch])

  useEffect(() => {
    if (clusterSettings.length > 0) {
      return
    }
    API.get('/cluster/info', axiosConfig)
      .then((result) => {
        dispatch(setClusterSettings(result.data.data))
      })
      .catch(err => {
        showSnackbar("Something went wrong !!")
      })
  }, [axiosConfig, clusterSettings.length, dispatch, showSnackbar])

  useEffect(() => {
    if (!settingsCluster) {
      return
    }
    const updatedCluster = enrichedClusters.find(({ clusterName }) => clusterName === settingsCluster.clusterName)
    if (!updatedCluster) {
      handleCloseClusterSettings()
      return
    }
    if (
      updatedCluster.displayIsInternal !== settingsCluster.displayIsInternal ||
      updatedCluster.displayIsOwned !== settingsCluster.displayIsOwned ||
      updatedCluster.displayIsProduction !== settingsCluster.displayIsProduction
    ) {
      setSettingsCluster(updatedCluster)
    }
  }, [enrichedClusters, settingsCluster, handleCloseClusterSettings])

  useEffect(() => {
    if (settingsAnchorEl && !settingsAnchorEl.isConnected) {
      handleCloseClusterSettings()
    }
  }, [enrichedClusters, settingsAnchorEl, handleCloseClusterSettings])

  const dashboardColumns = useMemo(() => {
    return HomePageColumns.map((column) => {
      if (column.field !== 'clusterName') {
        return column
      }
      const baseRender = column.renderCell
      return {
        ...column,
        renderCell: (cellValue) => (
          <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              {baseRender ? baseRender(cellValue) : cellValue.value}
            </Box>
            <Tooltip title="Cluster settings">
              <IconButton
                size="small"
                onClick={(event) => handleOpenClusterSettings(event, cellValue.row)}
              >
                <SettingsOutlinedIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          </Stack>
        )
      }
    })
  }, [handleOpenClusterSettings])

  const tabData = [
    {
      label: 'Production clusters',
      component: <DataDisplay
        rows={getProductionClustersData()}
        columns={dashboardColumns}
        loading={isLoading}
        handleCellClick={handleCellClick}
        sortModel={prodSortModel}
        onSortModelChange={handleProdSortModelChange}
      />
    },
    {
      label: 'POV clusters',
      component: <DataDisplay
        rows={getPOVClustersData()}
        columns={dashboardColumns}
        loading={isLoading}
        handleCellClick={handleCellClick}
        sortModel={povSortModel}
        onSortModelChange={handlePovSortModelChange}
      />
    }
  ]

  return (
    <>
      <Grid container spacing={2}>
        <Grid item lg={12} md={12}>
          <Navbar rightWidgetList={[
            <DrawerMenu />,
            <Search>
              <SearchIconWrapper>
                <SearchIcon />
              </SearchIconWrapper>
              <StyledInputBase
                value={searchCluster}
                onChange={(e) => {
                  setSearchCluster(e.target.value)
                }}
                placeholder="Search cluster…"
                inputProps={{ 'aria-label': 'search' }}
              />
            </Search>,
            <Button variant='contained'
            color='secondary' disabled={disableViewStateButton} onClick={viewStateButtonClickHandler}>{viewStateButtonText}</Button>
          ]} />
        </Grid>
        <Grid item lg={12} md={12} sx={{ display: 'flex', alignItems: 'start' }}>
          <NumberDisplayCard color={'blue'} variant='h3' infoText={'Total Scanned Count across all Clusters'} count={get_total_scanned_count()} />
          <NumberDisplayCard color={'green'} variant='h3' infoText={'Total Data Monitored across all Clusters'} count={get_total_data_monitored()} />
          <InfoDisplayCard variant='h5' infoList={syncInfo} />
        </Grid>
        <Grid item lg={12} md={12} sx={{ padding: '10px' }}>
          {/* <Typography variant='h5' color={'#313638'} sx={{ marginLeft: '15px', fontWeight: 800 }}>{"Clusters"}</Typography> */}
        </Grid>
        <Grid item lg={12} md={12}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: 1, borderColor: tokens.colors.border, px: 1 }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="clusters tabs"
              variant='scrollable'
              TabIndicatorProps={{ style: { height: 3, borderRadius: 2, background: tokens.colors.primary } }}
              sx={{ flexGrow: 1 }}
            >
              {tabData.map((info, index) => (
                <Tab
                  label={info.label}
                  {...a11yProps(index)}
                  key={info.label}
                  sx={{
                    textTransform: 'uppercase',
                    fontWeight: 800,
                    color: tokens.colors.textSecondary,
                    '&.Mui-selected': { color: tokens.colors.primary },
                    minHeight: 44
                  }}
                />
              ))}
            </Tabs>
            <Button
              variant='contained'
              color='secondary'
              sx={{ ml: 2, whiteSpace: 'nowrap' }}
              disabled={!clusterSettingsDirty || isSavingClusterSettings || clusterSettings.length === 0}
              onClick={handleSaveClusterSettings}
            >
              {isSavingClusterSettings ? 'Saving…' : 'Apply toggles'}
            </Button>
          </Box>
        </Grid>
        {tabData.map((info, index) => {
          let Component = info.component.type
          return (
            <React.Fragment key={info.label}>
              <CustomTabPanel value={tabValue} index={index} sx={{ minHeight: 'auto' }}>
                <Component {...info.component.props} height={480} noRowsText={index === 0 ? 'No production clusters to display' : 'No POV clusters to display'} />
              </CustomTabPanel>
            </React.Fragment>
          )
        })}
      </Grid>
      <Popover
        open={Boolean(settingsAnchorEl)}
        anchorEl={settingsAnchorEl}
        onClose={handleCloseClusterSettings}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        PaperProps={{ sx: { p: 2, width: 240 } }}
      >
        <Stack spacing={1.5}>
          <Box>
            <Typography variant='subtitle2' sx={{ fontWeight: 700 }}>
              {settingsCluster?.clusterName || 'Cluster settings'}
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              Toggle flags below, then hit apply near the tabs.
            </Typography>
          </Box>
          <ClusterSettingsInline
            cluster={settingsCluster}
            onToggle={handleClusterStateChange}
            disabled={isSavingClusterSettings || clusterSettings.length === 0}
          />
        </Stack>
      </Popover>
    </>
  );
};


export default Dashboard;
